import { type NoteAttributes } from './generated/NoteAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import { RelationAttribute } from '../RelationAttribute';
import Fragment from '../Fragment';
import { isEmpty } from 'lodash';
import { DocumentTypeEnum } from '../Enums';

/**
 * Note model for things like feature description etc. This model does not stand
 * alone, it is always attached to a field on another model, and so calling
 * `.save()` or `.persisted` will delegate to the parent.
 */
interface Note extends NoteAttributes {}

@model
class Note extends ApplicationModel {
  // Start auto-generated
  static typename: 'Note' = 'Note';
  get typename(): 'Note' {
    return 'Note';
  }

  static internalTypename: 'Note' = 'Note';
  get internalTypename(): 'Note' {
    return 'Note';
  }

  static fields = {
    annotationsCount: attr({ readable: true }),
    canReviewNote: attr({ readable: true }),
    canUpdateNote: attr({ readable: true }),
    editorProperties: attr({ readable: true, json: true }),
    editorVersion: attr({ readable: true }),
    htmlBody: attr({ readable: true }),
    id: attr({ readable: true }),
    markdownBody: attr({ readable: true }),
    usedTemplateIds: attr({ readable: true }),
    notable: attr.belongsTo({
      readable: true,
      typename: [
        'AiUserAgent',
        'Attachment',
        'BusinessModel',
        'BusinessModelComponent',
        'DiscoveryFile',
        'DiscoveryInterview',
        'DiscoveryScript',
        'DiscoveryScriptPromptUse',
        'DiscoveryStudy',
        'Epic',
        'Feature',
        'Goal',
        'IdeaOrganization',
        'Initiative',
        'NoteTemplate',
        'Page',
        'Poll',
        'ProgramIncrement',
        'Project',
        'ProjectStrategyComponent',
        'Release',
        'Requirement',
      ],
    }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    annotations: attr.hasMany({ readable: true }),
    attachments: attr.hasMany({ readable: true }),
    authors: attr.hasMany({ readable: true }),
    whiteboards: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  /**
   * When a parent record sets a note field allow the value to be a Note, the
   * note attributes or a string. All of these end up being written as a Note
   * object.
   *
   * @param {ApplicationModel} parent
   * @param {string} fieldName
   * @param {*} value
   */
  static onParentSet(parent, fieldName, value) {
    // If settings to null/undefined then just return
    if (!value) return value;

    /** @type {Note} */
    let note;

    if (value instanceof Note) {
      note = value;
    } else if (typeof value === 'object') {
      note = new Note(value, {
        parent: new RelationAttribute(parent, fieldName),
      });
    } else {
      note =
        parent.getAttribute(fieldName) ||
        new Note({}, { parent: new RelationAttribute(parent, fieldName) });
      // Value goes into htmlBody
      note.htmlBody = value;
    }

    if (note.isDirty()) {
      parent.flagDirty(fieldName);
    }

    return note;
  }

  /**
   * When the parent of this note is saved send the note body instead of the
   * whole note object
   *
   * @param {*} parent
   */
  valueForParentMutation(parent) {
    return this.getAttribute('htmlBody');
  }

  flagDirty(name) {
    if (this._parent?.attribute) {
      this._parent.flagDirty(this._parent.attribute);
    }

    return super.flagDirty(name);
  }

  get persisted() {
    if (this._parent) return true;
    return super.persisted;
  }

  async save() {
    return this._parent.save();
  }

  /**
   * Link the use of a template in this Note for display in the
   * Best Practices panel.
   * @param templateId The ID of the template to link.
   * @returns Whether the mutation was successful.
   */
  static async linkToTemplate(
    id: string,
    templateId: string
  ): Promise<Boolean> {
    const data = await ApplicationModel.mutate(
      new Fragment('linkTemplateToDocument')
        .argument('id', id)
        .argument('type', DocumentTypeEnum.NOTE, true)
        .argument('templateId', templateId)
        .argument('__reactivelyUpdate', true)
    );

    return isEmpty(data.linkTemplateToDocument.errors);
  }
}

export default Note;
